import type {
  GetAccessTokenSilentlyType,
  GetAccessTokenWithPopupType
} from '@/types/transcriptionSessionTypes';

const API_BASE_URL = import.meta.env.VITE_API_BASE_URL || '/api';

export enum ThreadType {
  Chat = 0,
  SelfIntake = 1,
  GatedQuestionFilling = 2
}

export interface ThreadIdResult {
  transcriptionSessionId: string;
  userId: string;
  threadId?: string | null;
  created: boolean;
}

const THREAD_CACHE_KEY = 'lg_thread_ids';

function getThreadMapKey(sessionId: string, threadType: ThreadType): string {
  return `${sessionId}_${threadType}`;
}

export function loadThreadMap(): Record<string, string> {
  try {
    return JSON.parse(localStorage.getItem(THREAD_CACHE_KEY) || '{}');
  } catch {
    return {};
  }
}

export function saveThreadMap(map: Record<string, string>) {
  localStorage.setItem(THREAD_CACHE_KEY, JSON.stringify(map));
}

export function getCachedThreadId(
  sessionId: string,
  threadType: ThreadType = ThreadType.Chat
): string | undefined {
  const map = loadThreadMap();
  return map[getThreadMapKey(sessionId, threadType)];
}

export function cacheThreadId(
  sessionId: string,
  threadId: string,
  threadType: ThreadType = ThreadType.Chat
) {
  const map = loadThreadMap();
  map[getThreadMapKey(sessionId, threadType)] = threadId;
  saveThreadMap(map);
}

const getAuthToken = async (
  getAccessTokenSilently: GetAccessTokenSilentlyType,
  getAccessTokenWithPopup: GetAccessTokenWithPopupType
): Promise<string> => {
  const authParams = {
    authorizationParams: {
      audience: import.meta.env.VITE_API_AUTH0_AUDIENCE
    }
  };
  try {
    return await getAccessTokenSilently(authParams);
  } catch (error: any) {
    if (
      error?.error === 'consent_required' ||
      error?.error === 'login_required' ||
      error?.error === 'interaction_required'
    ) {
      const token = await getAccessTokenWithPopup(authParams);
      if (!token) throw new Error('Interactive token acquisition failed.');
      return token;
    }
    throw error;
  }
};

export async function fetchCurrentThreadId(
  sessionId: string,
  userId: string,
  getAccessTokenSilently: GetAccessTokenSilentlyType,
  getAccessTokenWithPopup: GetAccessTokenWithPopupType,
  threadType: ThreadType = ThreadType.Chat
): Promise<ThreadIdResult> {
  const token = await getAuthToken(
    getAccessTokenSilently,
    getAccessTokenWithPopup
  );

  const res = await fetch(
    `${API_BASE_URL}/transcriptionsessions/${sessionId}/threads/current?threadType=${threadType}`,
    {
      method: 'GET',
      headers: {
        Authorization: `Bearer ${token}`,
        'X-User-Id': userId
      }
    }
  );
  if (!res.ok) {
    throw new Error(`Failed to fetch thread id: ${res.status}`);
  }
  return (await res.json()) as ThreadIdResult;
}

export async function updateCurrentThreadId(
  sessionId: string,
  userId: string,
  threadId: string,
  getAccessTokenSilently: GetAccessTokenSilentlyType,
  getAccessTokenWithPopup: GetAccessTokenWithPopupType,
  threadType: ThreadType = ThreadType.Chat
): Promise<ThreadIdResult> {
  const token = await getAuthToken(
    getAccessTokenSilently,
    getAccessTokenWithPopup
  );

  const payload: ThreadIdResult = {
    transcriptionSessionId: sessionId,
    userId,
    threadId,
    created: false
  };

  const res = await fetch(
    `${API_BASE_URL}/transcriptionsessions/${sessionId}/threads/current?threadType=${threadType}`,
    {
      method: 'PUT',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`,
        'X-User-Id': userId
      },
      body: JSON.stringify(payload)
    }
  );
  if (!res.ok) {
    throw new Error(`Failed to update thread id: ${res.status}`);
  }
  return (await res.json()) as ThreadIdResult;
}
